import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isAfter,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import type { Locale } from 'date-fns';
import * as Sentry from '@sentry/vue';
import { enUS, fr } from 'date-fns/locale';
import i18n from '@/config/i18n';
import { API_DATE_FORMAT, DISPLAY_DATE_FORMAT } from '@/constants';
import type { CalendarSelectedMonth } from '@/model/calendar-selected-month';

export type DateInput = Date | string | number | null | undefined;

export const WEEK = { weekStartsOn: 1 } as const;

const LOCALES: Record<string, Locale> = { fr, en: enUS };

const LONG_DATE_FORMATS: Record<string, string> = {
  fr: 'EEEE dd MMMM yyyy',
  en: 'EEEE, MMMM d, yyyy',
};

const A_MONDAY = new Date(2024, 0, 1);

export function dateLocale(): Locale {
  return LOCALES[i18n.global.locale.value] ?? fr;
}

export function toDate(value: DateInput): Date {
  if (value instanceof Date || typeof value === 'number') {
    return new Date(value);
  }

  if (!value) {
    return new Date(NaN);
  }

  const date = parseISO(value);
  if (!isValid(date)) {
    Sentry.captureMessage('Date illisible', {
      level: 'warning',
      extra: { value },
    });
  }

  return date;
}

export function formatDate(
  value: DateInput,
  pattern: string = DISPLAY_DATE_FORMAT,
): string {
  const date = toDate(value);

  return isValid(date)
    ? format(date, pattern, { locale: dateLocale(), ...WEEK })
    : '';
}

export function formatLongDate(value: DateInput): string {
  return formatDate(
    value,
    LONG_DATE_FORMATS[i18n.global.locale.value] ?? LONG_DATE_FORMATS.fr,
  );
}

export function weekdayIndex(value: DateInput): number {
  return (getDay(toDate(value)) + 6) % 7;
}

export function weekdayNames(pattern: 'EEEE' | 'EEEEEE'): string[] {
  return Array.from({ length: 7 }, (unused, index) =>
    weekdayName(index, pattern),
  );
}

export function weekdayName(index: number, pattern: 'EEEE' | 'EEEEEE'): string {
  return format(addDays(A_MONDAY, index), pattern, { locale: dateLocale() });
}

export function monthName(
  monthIndex: number,
  pattern: 'MMMM' | 'MMM' = 'MMMM',
): string {
  return format(new Date(A_MONDAY.getFullYear(), monthIndex, 1), pattern, {
    locale: dateLocale(),
  });
}

export function todayApiDate(): string {
  return format(new Date(), API_DATE_FORMAT);
}

export function daysFromToday(value: DateInput): number {
  return differenceInCalendarDays(toDate(value), new Date());
}

export function notBeforeToday(value: DateInput): string {
  return daysFromToday(value) < 0
    ? todayApiDate()
    : formatDate(value, API_DATE_FORMAT);
}

export function firstDayOfMonth(month: CalendarSelectedMonth): Date {
  return new Date(month.year, month.monthIndex, 1);
}

export function monthGrid(date: Date): { start: Date; end: Date } {
  return {
    start: startOfWeek(startOfMonth(date), WEEK),
    end: endOfWeek(endOfMonth(date), WEEK),
  };
}

export function apiDatesBetween(
  dateFrom: DateInput,
  dateTo: DateInput,
): string[] {
  const start = toDate(dateFrom);
  const end = toDate(dateTo);
  if (!isValid(start) || !isValid(end) || isAfter(start, end)) {
    return [];
  }

  return eachDayOfInterval({ start, end }).map((day) =>
    format(day, API_DATE_FORMAT),
  );
}

export function isDayWithin(
  date: DateInput,
  dateFrom: DateInput,
  dateTo: DateInput,
): boolean {
  return isWithinInterval(startOfDay(toDate(date)), {
    start: startOfDay(toDate(dateFrom)),
    end: startOfDay(toDate(dateTo)),
  });
}

export function selectedMonthOf(date: DateInput): CalendarSelectedMonth {
  const day = toDate(date);

  return { monthIndex: day.getMonth(), year: day.getFullYear() };
}

export function timeToMinutes(time: string | null | undefined): number {
  const [hours, minutes] = (time ?? '').split(':').map(Number);

  return time ? hours * 60 + minutes : NaN;
}

export function minutesToTime(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function combineDateAndTime(date: DateInput, time: string): Date {
  return toDate(`${formatDate(date, API_DATE_FORMAT)} ${time}`);
}
